import { Event, useStore } from '@mirai/data-sources';
import { dateDiff, parseDate, useLocale } from '@mirai/locale';
import { Action, Icon, styles, Text, Tooltip, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { FORMAT_DATE } from './BookingSummary.constants';
import { L10N } from './BookingSummary.l10n';
import * as style from './BookingSummary.module.css';
import {
  EVENT,
  getAccommodationLabel,
  getGuestLabel,
  getRatesUrl,
  getRoomNumber,
  ICON,
  METRICS_EVENTS,
  OCCUPATION_TYPE,
  SUBMIT_DELAY,
} from '../../helpers';
import { ButtonMore } from '../ButtonMore';
import { PriceBreakdown } from '../PriceBreakdown';

const { ADULT, BABY, CHILD } = OCCUPATION_TYPE;

const getBookingRoomNames = (rooms = []) =>
  Object.values(
    rooms.reduce(
      (total, { amount, type } = {}) => ({
        ...total,
        ...(type
          ? {
              [type]: {
                amount: (total[type]?.amount || 0) + amount,
                type,
              },
            }
          : undefined),
      }),
      {},
    ),
  ).map(({ amount, type } = {}) => `${type}${amount > 1 ? ` (x${amount})` : ''}`);

const getBookingOccupationLabel = ({ amount = 0, translate, type }) =>
  `${amount} ${getGuestLabel({ plural: amount !== 1, type }, translate)}`;

const getBookingAccommodationLabel = ({ amount = 0, translate, type }) =>
  `${amount} ${getAccommodationLabel({ plural: amount !== 1, type }, translate).toLowerCase()}`;

const getPositiveLabels = (items = []) => items.filter(({ amount } = {}) => amount > 0).map(({ label }) => label);

const BookingSummary = ({ box = false, children, dataSource = {}, readOnly = false, ...others }) => {
  const { isDesktop } = useDevice();
  const { dateFormat, translate } = useLocale();
  const {
    value: {
      components = {},
      finder: { promocode = '' } = {},
      hotel: { accommodationType: hotelAccommodationType, features = {}, id, variants = {} } = {},
      language,
      urlParams: { roomNumberBySelectedRoom, ...urlParams } = {},
      variant,
    },
  } = useStore();

  const [expanded, setExpanded] = useState(isDesktop);

  const {
    checkIn,
    checkInTime,
    checkOut,
    checkOutTime,
    currency,
    details = [],
    cancellationPolicy: { caption: cancellationCaption, name: cancellationName } = {},
    extras = [],
    hotel: { address, city, country, email, location = [], name: hotelName = '', phone, site } = {},
    nights,
  } = dataSource;

  const handleBack = () => {
    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.CHECKOUT_INTERACTION,
      action: 'button_click',
      element_clicked: 'change_selection',
    });
    setTimeout(() => {
      document.location.href = getRatesUrl({ currency, language, nights, promocode, variant, ...urlParams });
    }, [SUBMIT_DELAY]);
  };

  const handlePress = () => {
    setExpanded(true);
    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.CHECKOUT_INTERACTION,
      action: 'button_click',
      element_clicked: 'view_more',
    });
  };

  const { days } = dateDiff(parseDate(checkIn), parseDate(checkOut));
  const isCheckout = !!components.checkout;
  const items = details.filter(({ type } = {}) => !!type) || [];
  const roomNumber = getRoomNumber(roomNumberBySelectedRoom);
  const urlLocation = location.length ? `https://maps.google.com?q=${location.join(',')}` : undefined;
  const { name = hotelName, type } = features[id] || {};
  const { accommodationType = hotelAccommodationType } = variants[id] || {};
  const bookingDetails = details.reduce(
    (total, { amount, extras = [], occupation: { adults = 0, babies = 0, children = 0 } = {}, rooms } = {}) => {
      const roomAmount = rooms || amount || 1;

      return {
        adults: total.adults + adults * roomAmount,
        babies: total.babies + babies * roomAmount,
        children: total.children + children * roomAmount,
        extras: [...total.extras, ...extras],
        rooms: total.rooms + roomAmount,
      };
    },
    { adults: 0, babies: 0, children: 0, extras: [], rooms: 0 },
  );
  const bookingRoomNames = getBookingRoomNames(details);
  const bookingExtras = [...bookingDetails.extras, ...extras];
  const bookingExtraNames = bookingExtras.map(({ title } = {}) => title).filter(Boolean);
  const bookingSummaryLabels = getPositiveLabels([
    { amount: nights || days || 0, label: translate(L10N.LABEL_NIGHT_COUNT, { value: nights || days || 0 }) },
    {
      amount: bookingDetails.rooms,
      label: getBookingAccommodationLabel({ amount: bookingDetails.rooms, translate, type: accommodationType }),
    },
    { amount: bookingExtras.length, label: translate(L10N.LABEL_EXTRA_COUNT, { value: bookingExtras.length }) },
  ]);
  const bookingOccupationLabels = getPositiveLabels([
    {
      amount: bookingDetails.adults,
      label: getBookingOccupationLabel({ amount: bookingDetails.adults, translate, type: ADULT }),
    },
    {
      amount: bookingDetails.children,
      label: getBookingOccupationLabel({ amount: bookingDetails.children, translate, type: CHILD }),
    },
    {
      amount: bookingDetails.babies,
      label: getBookingOccupationLabel({ amount: bookingDetails.babies, translate, type: BABY }),
    },
  ]);

  const showCancellation = cancellationName || cancellationCaption;

  return (
    <View {...others} wide className={styles(style.bookingSummary, box && style.box, others.className)}>
      <View className={style.section}>
        <Text bold headline={isDesktop}>
          {translate(L10N.LABEL_BOOKING_SUMMARY)}
        </Text>

        <View row wide className={style.dates}>
          <View>
            <Text bold>{translate(L10N.LABEL_CHECK_IN)}</Text>
            <View>
              <Text action>{dateFormat(parseDate(checkIn), FORMAT_DATE)}</Text>
              {checkInTime && (
                <Text light small>
                  {translate(L10N.LABEL_CHECKIN_TIME, { value: checkInTime })}
                </Text>
              )}
            </View>
          </View>

          <View>
            <Text bold>{translate(L10N.LABEL_CHECK_OUT)}</Text>
            <View>
              <Text action>{dateFormat(parseDate(checkOut), FORMAT_DATE)}</Text>
              {checkOutTime && (
                <Text light small>
                  {translate(L10N.LABEL_CHECKOUT_TIME, { value: checkOutTime })}
                </Text>
              )}
            </View>
          </View>
        </View>

        <View>
          <Text bold>{translate(L10N.LABEL_YOUR_BOOKING)}</Text>
          {bookingSummaryLabels?.length > 0 && <Text action>{bookingSummaryLabels.join(', ')}</Text>}
          {bookingOccupationLabels?.length > 0 && <Text action>{bookingOccupationLabels.join(', ')}</Text>}
          {roomNumber ? (
            items.map(({ amount, type } = {}, index) => (
              <View key={index} row className={style.info}>
                <Text light small>
                  {`${type}${roomNumber ? ` (${translate(L10N.LABEL_ROOM_NUMBER, { roomNumber })})` : ''} ${
                    amount > 1 ? ` (x${amount})` : ''
                  }`}
                </Text>
                {roomNumber && (
                  <Tooltip pressable text={translate(L10N.LABEL_SUBJECT_TO_AVAILABILITY)}>
                    <Icon value={ICON.INFO} />
                  </Tooltip>
                )}
              </View>
            ))
          ) : (
            <Text light small>
              {bookingRoomNames.join(', ')}
            </Text>
          )}
          {bookingExtraNames?.length > 0 && (
            <Text light small>
              {bookingExtraNames.join(', ')}
            </Text>
          )}
          {showCancellation && (
            <View>
              {cancellationName && (
                <Text action bold success>
                  {cancellationName}
                </Text>
              )}
              {cancellationCaption && (
                <Text light small>
                  {cancellationCaption}
                </Text>
              )}
            </View>
          )}
        </View>

        <View>
          <Text bold>{translate(L10N.LABEL_ACCOMMODATION)}</Text>
          <Text action markdown={false}>
            {`${type && name ? translate(L10N.LABEL_PROPERTY_TYPE, { type }) : ''} ${name}`?.trim()}
          </Text>
          <Text light small>
            {`${address}, ${city}, ${country}`}
          </Text>
          {!isCheckout && (
            <Text light small>
              {phone}
            </Text>
          )}
        </View>

        {!isCheckout && (site || email || urlLocation) && (
          <View row wide className={styles(style.links, style.noPrint)}>
            {site && (
              <Action href={site} underline small target="_blank">
                <Icon value={ICON.SITE} />
                {translate(L10N.ACTION_VIEW_HOTEL)}
              </Action>
            )}
            {email && (
              <Action href={`mailto:${email}`} underline small target="_blank">
                <Icon value={ICON.MAIL} />
                {translate(L10N.ACTION_SEND_EMAIL)}
              </Action>
            )}
            {urlLocation && (
              <Action href={urlLocation} underline small target="_blank">
                <Icon value={ICON.LOCATION} />
                {translate(L10N.ACTION_VIEW_MAP)}
              </Action>
            )}
          </View>
        )}

        {isCheckout && !readOnly && (
          <Action underline onPress={handleBack} testId="action-change-selection">
            {translate(L10N.ACTION_CHANGE_SELECTION)}
          </Action>
        )}
      </View>

      <PriceBreakdown dataSource={dataSource} notifications={!readOnly} className={style.section} />

      {children ? (
        expanded ? (
          <View className={style.section}>{children}</View>
        ) : (
          <ButtonMore onPress={handlePress} />
        )
      ) : undefined}
    </View>
  );
};

BookingSummary.displayName = 'Mirai:Core:BookingSummary';

BookingSummary.propTypes = {
  box: PropTypes.bool,
  children: PropTypes.any,
  dataSource: PropTypes.shape({}),
  readOnly: PropTypes.bool,
};

export { BookingSummary };
